import React, { useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Phone, Paperclip, Menu, X, Lock, Sparkles } from "lucide-react";

import HowItWorks from "@/components/LandingPageComponents/HowItWorks";
import WhoIsItFor from "@/components/LandingPageComponents/WhoIsItFor";
import WhyYouShould from "@/components/LandingPageComponents/WhyYouShould";
import ContactSection from "@/components/LandingPageComponents/ContactSection";

export default function LandingPage() {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [toast, setToast] = useState(false);
  const toastTimerRef = useRef(null);
  const navigate = useNavigate();

  // Change this to true when Login / Register / Plans are ready.
  const AUTH_RELEASED = false;

  const showLockedToast = () => {
    setToast(true);

    if (toastTimerRef.current) {
      clearTimeout(toastTimerRef.current);
    }

    toastTimerRef.current = setTimeout(() => {
      setToast(false);
    }, 3600);
  };

  const handleLockedRoute = (path) => {
    if (!AUTH_RELEASED) {
      showLockedToast();
      return;
    }

    navigate(path);
  };

  const scrollToSection = (sectionId) => {
    setIsMobileMenuOpen(false);

    if (sectionId === "home") {
      window.history.replaceState(null, "", "/");

      window.scrollTo({
        top: 0,
        behavior: "smooth",
      });

      return;
    }

    const section = document.getElementById(sectionId);

    if (!section) return;

    window.history.replaceState(null, "", `#${sectionId}`);

    const navbarOffset = 95;
    const sectionTop =
      section.getBoundingClientRect().top + window.scrollY - navbarOffset;

    window.scrollTo({
      top: sectionTop,
      behavior: "smooth",
    });
  };

  const navButtonClass =
    "text-sm font-medium text-gray-500 hover:text-gray-900 transition-colors bg-transparent border-0 outline-none cursor-pointer p-0";

  const mobileNavButtonClass =
    "text-left text-base font-medium text-gray-600 hover:text-gray-900 transition-colors bg-transparent border-0 outline-none cursor-pointer p-0";

  return (
    <div className="min-h-screen landing-gradient-bg font-sans text-gray-900 overflow-x-hidden relative">
      {/* ================= TOAST ================= */}
      {toast && (
        <div className="fixed top-24 left-1/2 -translate-x-1/2 z-[9999] px-4 w-full max-w-md pointer-events-none">
          <div className="relative overflow-hidden rounded-3xl bg-white/90 backdrop-blur-xl border border-white/80 shadow-[0_24px_70px_rgba(15,23,42,0.18)] p-4">
            <div className="absolute -top-10 -right-10 w-32 h-32 rounded-full bg-blue-500/10 blur-2xl" />

            <div className="relative flex items-start gap-4">
              <div className="w-11 h-11 rounded-2xl bg-slate-900 text-white flex items-center justify-center shrink-0 shadow-lg">
                <Lock className="w-5 h-5" />
              </div>

              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <h4 className="text-sm font-extrabold text-slate-900">
                    Hold on, my friend
                  </h4>

                  <Sparkles className="w-4 h-4 text-blue-600" />
                </div>

                <p className="mt-1 text-sm leading-6 text-slate-600">
                  I got you. SlipSense is getting ready. We’ll release it soon
                  with affordable plans for serious businesses.
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ================= NAVBAR ================= */}
      <nav className="fixed top-5 left-0 right-0 z-[999] pointer-events-none">
        <div className="mx-auto w-full max-w-7xl px-4 md:px-8 flex items-center justify-between">
          {/* Logo */}
          <button
            type="button"
            onClick={() => scrollToSection("home")}
            className="flex items-center gap-2 bg-transparent border-0 outline-none cursor-pointer p-0 pointer-events-auto"
          >
            <div className="w-8 h-8 bg-blue-600 rounded-md flex items-center justify-center shadow-sm">
              <span className="text-white font-bold text-xl">S</span>
            </div>

            <span className="font-bold text-xl tracking-tight text-slate-800">
              SlipSense
            </span>
          </button>

          {/* Desktop Center Pill */}
          <div className="hidden lg:flex items-center gap-8 bg-white/85 backdrop-blur-xl px-8 py-3 rounded-full shadow-[0_18px_45px_rgba(15,23,42,0.07)] border border-white/80 pointer-events-auto">
            <button
              type="button"
              onClick={() => scrollToSection("home")}
              className="text-sm font-semibold text-gray-900 bg-transparent border-0 outline-none cursor-pointer p-0"
            >
              Home
            </button>

            <button
              type="button"
              onClick={() => scrollToSection("how-it-works")}
              className={navButtonClass}
            >
              How it works
            </button>

            <button
              type="button"
              onClick={() => scrollToSection("who-is-it-for")}
              className={navButtonClass}
            >
              Who is it for
            </button>

            <button
              type="button"
              onClick={() => scrollToSection("why-use-it")}
              className={navButtonClass}
            >
              Why you should use it
            </button>

            <button
              type="button"
              onClick={() => scrollToSection("contact")}
              className={navButtonClass}
            >
              Contact
            </button>
          </div>

          {/* Desktop Auth */}
          <div className="hidden lg:flex items-center gap-4 pointer-events-auto">
            <button
              type="button"
              onClick={() => handleLockedRoute("/login")}
              className="inline-flex items-center gap-2 text-sm font-medium text-gray-600 hover:text-gray-900 transition-colors bg-transparent border-0 outline-none cursor-pointer"
            >
              <Lock className="w-3.5 h-3.5" />
              Login
            </button>

            <button
              type="button"
              onClick={() => handleLockedRoute("/register")}
              className="inline-flex items-center gap-2 px-6 py-2.5 text-sm font-semibold text-white bg-[#1a1a1a] rounded-full hover:bg-black transition-all shadow-[0_14px_30px_rgba(15,23,42,0.18)]"
            >
              <Lock className="w-3.5 h-3.5" />
              View Plans
            </button>
          </div>

          {/* Mobile Menu Button */}
          <button
            type="button"
            className="lg:hidden p-2 text-slate-800 z-50 pointer-events-auto bg-white/85 backdrop-blur-xl rounded-full shadow-md"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          >
            {isMobileMenuOpen ? (
              <X className="w-6 h-6" />
            ) : (
              <Menu className="w-6 h-6" />
            )}
          </button>

          {/* Mobile Menu */}
          {isMobileMenuOpen && (
            <div className="absolute top-14 left-4 right-4 bg-white/95 backdrop-blur-xl rounded-2xl shadow-2xl border border-white/80 p-6 flex flex-col gap-4 lg:hidden z-40 pointer-events-auto">
              <button
                type="button"
                onClick={() => scrollToSection("home")}
                className="text-left text-base font-semibold text-gray-900 bg-transparent border-0 outline-none cursor-pointer p-0"
              >
                Home
              </button>

              <button
                type="button"
                onClick={() => scrollToSection("how-it-works")}
                className={mobileNavButtonClass}
              >
                How it works
              </button>

              <button
                type="button"
                onClick={() => scrollToSection("who-is-it-for")}
                className={mobileNavButtonClass}
              >
                Who is it for
              </button>

              <button
                type="button"
                onClick={() => scrollToSection("why-use-it")}
                className={mobileNavButtonClass}
              >
                Why you should use it
              </button>

              <button
                type="button"
                onClick={() => scrollToSection("contact")}
                className={mobileNavButtonClass}
              >
                Contact
              </button>

              <div className="w-full h-px bg-gray-100 my-2" />

              <button
                type="button"
                onClick={() => handleLockedRoute("/login")}
                className="inline-flex items-center justify-center gap-2 text-base font-medium text-gray-600 bg-transparent border-0 outline-none cursor-pointer"
              >
                <Lock className="w-4 h-4" />
                Login
              </button>

              <button
                type="button"
                onClick={() => handleLockedRoute("/register")}
                className="inline-flex items-center justify-center gap-2 w-full py-3 text-sm font-semibold text-white bg-[#1a1a1a] rounded-xl text-center shadow-md"
              >
                <Lock className="w-4 h-4" />
                View Plans
              </button>
            </div>
          )}
        </div>
      </nav>

      {/* ================= HERO SECTION ================= */}
      <section
        id="home"
        className="relative w-full pt-28 sm:pt-32 pb-10 flex flex-col items-center text-center px-4 sm:px-6 md:px-8 min-h-screen justify-center overflow-hidden"
      >
        {/* Left Floating Card */}
        <div className="absolute top-[58%] sm:top-[50%] left-[-10%] sm:left-[-5%] md:left-[5%] lg:left-[10%] xl:left-[15%] hidden sm:block -rotate-[15deg] z-0 scale-75 sm:scale-90 md:scale-100">
          <div className="cast-shadow-sm" />

          <div className="floating-paper bg-white p-3 sm:p-4 rounded-xl w-40 sm:w-48 border border-gray-50 shadow-sm">
            <div className="text-[9px] sm:text-[10px] text-gray-400 font-bold mb-1 uppercase tracking-widest">
              Net Pay
            </div>

            <div className="text-lg sm:text-2xl font-extrabold text-gray-800 mb-2">
              ₹42,500
            </div>

            <div className="w-full h-1 bg-gray-100 rounded-full mb-1.5" />
            <div className="w-2/3 h-1 bg-gray-100 rounded-full" />
          </div>
        </div>

        {/* Right Floating Card */}
        <div className="absolute top-[20%] sm:top-[30%] right-[-10%] sm:right-[-5%] md:right-[5%] lg:right-[8%] xl:right-[12%] hidden sm:block rotate-[12deg] z-0 scale-75 sm:scale-90 md:scale-100">
          <div className="cast-shadow-sm" />

          <div className="floating-paper bg-white p-3 sm:p-4 rounded-2xl w-48 sm:w-56 border border-gray-50 shadow-sm">
            <div className="flex items-center gap-2 sm:gap-3 mb-2">
              <div className="bg-[#e8f5e9] p-1 sm:p-1.5 rounded-full">
                <Phone className="w-3 h-3 sm:w-4 sm:h-4 text-[#128c7e] fill-[#128c7e]" />
              </div>

              <div className="text-[10px] sm:text-xs font-bold text-gray-800">
                WhatsApp
              </div>
            </div>

            <div className="text-[9px] sm:text-[11px] text-gray-500 leading-relaxed font-medium">
              Your payslip for this month has been generated.{" "}
              <span className="text-blue-500 cursor-pointer">View PDF</span>
            </div>
          </div>
        </div>

        {/* Paperclip */}
        <div className="absolute top-[68%] right-[2%] md:right-[6%] hidden lg:block rotate-[35deg] opacity-60 drop-shadow-2xl z-0">
          <Paperclip
            className="w-12 h-12 md:w-16 md:h-16 text-gray-400"
            strokeWidth={1}
          />
        </div>

        {/* Center Content */}
        <div className="relative w-full max-w-4xl mx-auto flex flex-col items-center z-10 mt-8 sm:mt-12 md:mt-16">
          {/* Sticky Note */}
          <div
            className="absolute z-30 rotate-[8deg] left-1/2 -translate-x-[55%]
            -top-10 w-32 h-32
            sm:-top-12 sm:w-40 sm:h-40
            md:-top-14 md:w-48 md:h-48
            lg:-top-16 lg:w-52 lg:h-52
            xl:-top-20 xl:w-56 xl:h-52"
          >
            <div className="cast-shadow-main" />

            <div
              className="relative w-full h-full bg-[#fce94f] p-3 sm:p-4 md:p-5 lg:p-6 flex items-center justify-center text-center shadow-[inset_-3px_-3px_10px_rgba(0,0,0,0.03)] border-b border-r border-[#e5d443]"
              style={{ borderBottomRightRadius: "24px 6px" }}
            >
              <p
                className="text-gray-800 leading-tight transform -rotate-[2deg] opacity-90 text-[12px] sm:text-sm md:text-lg lg:text-xl xl:text-[1.4rem]"
                style={{ fontFamily: "'Caveat', 'Comic Sans MS', cursive" }}
              >
                The smartest
                <br />
                payroll system
                <br />
                for India.
              </p>
            </div>
          </div>

          {/* Main Headline */}
          <div className="relative z-20 w-full text-center mt-16 sm:mt-20 md:mt-24 lg:mt-28">
            <h1 className="font-medium tracking-tight text-slate-900 mb-4 md:mb-5 leading-[1.12] md:leading-[1.03] text-4xl sm:text-5xl md:text-6xl lg:text-[4rem] xl:text-[4.25rem]">
              Handle All Your <br className="hidden sm:block" />
              Business Payroll in{" "}
              <span className="relative inline-block mt-2 sm:mt-0">
                One Place
                <svg
                  className="absolute -bottom-2 sm:-bottom-4 left-0 w-full overflow-visible text-[#ff6b00]"
                  viewBox="0 0 300 20"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    className="sketch-underline"
                    d="M5 15C80 5 220 -2 295 12"
                    stroke="currentColor"
                    strokeWidth="4"
                    strokeLinecap="round"
                  />

                  <path
                    d="M280 8C288 12 293 14 295 12C292 8 285 4 280 8Z"
                    fill="currentColor"
                  />
                </svg>
              </span>
            </h1>

            <p className="text-sm sm:text-base md:text-lg text-gray-500 mb-6 md:mb-7 max-w-2xl mx-auto leading-relaxed mt-5 sm:mt-7 font-normal px-2">
              Nothing valuable is truly free. SlipSense helps you run payroll
              smoothly, avoid salary confusion, and manage your business without
              stress — at a price small businesses can afford.
            </p>

            <button
              type="button"
              onClick={() => handleLockedRoute("/register")}
              className="inline-flex items-center justify-center gap-2 px-6 sm:px-8 py-3 sm:py-3.5 text-sm font-semibold text-white bg-[#1a1a1a] rounded-full hover:bg-black transition-transform hover:scale-105 shadow-[0_15px_35px_rgba(0,0,0,0.2)]"
            >
              <Lock className="w-4 h-4" />
              View Affordable Plans
            </button>
          </div>
        </div>
      </section>

      {/* ================= TRUSTED BY ================= */}
      <section className="relative z-20 w-full bg-[#f4f5f7] px-4 sm:px-6 md:px-8 pt-4 pb-20">
        <div className="mx-auto max-w-5xl text-center">
          <p className="text-xs sm:text-sm text-gray-400 font-medium mb-6 sm:mb-8">
            Trusted by growing Indian companies
          </p>

          <div className="flex flex-wrap justify-center items-center gap-6 sm:gap-10 md:gap-20 opacity-40 grayscale">
            <span className="text-lg sm:text-xl font-bold font-serif tracking-tight">
              RetailShops
            </span>

            <span className="text-lg sm:text-xl font-bold font-mono">
              Agencies
            </span>

            <span className="text-lg sm:text-xl font-bold">
              Clinics & Med
            </span>

            <span className="text-lg sm:text-xl font-bold italic">
              Contractors
            </span>
          </div>
        </div>
      </section>

      {/* ================= LANDING SECTIONS ================= */}
      <HowItWorks />
      <WhoIsItFor />
      <WhyYouShould />
      <ContactSection />
    </div>
  );
}